/**
 * AuthGuard Component
 * 
 * Ensures authentication is validated before rendering protected content.
 * - Checks for valid JWT token before rendering children
 * - Shows loading state during auth verification
 * - Redirects to login immediately if not authenticated
 */

import { useEffect } from 'react';
import { useLocation, useNavigate } from '@/lib/router';
import { Spin } from 'antd';
import { useAuth } from '@/context/AuthContext';

/**
 * Parse JWT token to extract payload
 */
const parseJwt = (token: string): { exp: number } | null => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

/**
 * Check if JWT token is expired
 */
const isTokenExpired = (token: string): boolean => {
  const payload = parseJwt(token);
  if (!payload) return true;
  // Add 5 second buffer to account for clock skew
  return payload.exp * 1000 < Date.now() - 5000;
};

/**
 * Check if user has a valid auth token
 */
export const hasValidToken = (): boolean => {
  const token = localStorage.getItem('token');
  if (!token) return false;
  return !isTokenExpired(token);
};

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * AuthGuard wraps protected routes and ensures auth is validated
 * before rendering any content.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { initialState, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check auth status and redirect if needed
  useEffect(() => {
    // Still loading - wait
    if (loading) return;

    // Check token validity first (synchronous)
    const token = localStorage.getItem('token');
    
    if (!token) {
      // No token - redirect to login
      navigate('/login', { replace: true, state: { from: location.pathname } });
      return;
    }

    if (isTokenExpired(token)) {
      // Token expired - clear and redirect
      localStorage.removeItem('token');
      localStorage.removeItem('dashboard-latest-data');
      localStorage.removeItem('dashboard-last-fetch');
      navigate('/login', { replace: true, state: { from: location.pathname } });
      return;
    }

    // Token exists but user info not loaded - likely API failure
    if (!initialState?.currentUser) {
      navigate('/login', { replace: true, state: { from: location.pathname } });
      return;
    }
  }, [loading, initialState, navigate, location.pathname]);

  // Show loading spinner while auth is being checked
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f0f2f5'
      }}>
        <Spin size="large" tip="Verifying authentication..." />
      </div>
    );
  }

  // Check token validity synchronously before render
  const token = localStorage.getItem('token');
  if (!token || isTokenExpired(token) || !initialState?.currentUser) {
    // Return loading state while redirect happens in useEffect
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f0f2f5'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // Auth is valid - render children
  return <>{children}</>;
}

export default AuthGuard;
